// Выбор в семье механизмов апвеллинга: чем закрасить и что наложить линиями.
//
// ОДИН РАСТР + ОСТАЛЬНЫЕ ЛИНИЯМИ (решение владельца 2026-08-31). Буквальная
// стопка полупрозрачных растров отвергнута: два знаковых поля, включённых
// сразу, уже нечитаемы — там, где одно синее, а другое коричневое, глаз видит
// третий цвет и читает его как значение.
//
// Список механизмов строится ИЗ КАТАЛОГА слоёв (`group === 'upwelling'`), а не
// из захардкоженного перечня здесь: перечень отстанет от каталога, и
// добавленный на сервере механизм в панели не появится, а снятый оставит
// мёртвый тумблер, отвечающий 400.

/** Механизм, как его описывает каталог слоёв кабинета. */
export interface Mechanism {
  id: string
  unit: string
  has_depth: boolean
}

export interface UpwellingSelection {
  /** Чем закрашено поле. Пустая строка — механизм не выбирали. */
  raster: string
  /** Что наложено линиями. Заливка сюда не входит по построению. */
  iso: string[]
  /** Рисовать ли нулевую линию (границу подъёма и опускания). */
  zero: boolean
}

/** Главный слой раздела. `wo` — эталон, посчитанный NEMO: в нём Кориолис,
 *  обтекание рельефа, дивергенция и вихревая накачка уже учтены правильно. */
export const DEFAULT_RASTER = 'upw_vertical'

/** Замороженный индекс. В семье он есть (его показывают рядом, чтобы видеть,
 *  что убрал гейт Росби), но лицом раздела быть перестал. */
export const LEGACY = 'upwelling'

/** Механизмы семьи из каталога слоёв, в порядке каталога. */
export function mechanismsFrom(layers: { id: string; unit: string; has_depth: boolean; group?: string }[]): Mechanism[] {
  return layers
    .filter(l => l.group === 'upwelling')
    .map(l => ({ id: l.id, unit: l.unit, has_depth: l.has_depth }))
}

/**
 * Привести выбор к тому, что каталог действительно предлагает.
 *
 * При ПУСТОМ каталоге возвращает вход ТЕМ ЖЕ объектом: каталог приезжает
 * запросом, и до его приезда обнулять выбор нельзя — ссылка `?upw=upw_eddy`
 * открылась бы на эталоне, а новый объект на каждый кадр перезапускал бы
 * эффекты карты (та же беда, из-за которой написан `clampOverlays`).
 */
export function clampSelection(
  sel: UpwellingSelection,
  catalog: Mechanism[],
): UpwellingSelection {
  if (catalog.length === 0) return sel
  const known = new Set(catalog.map(m => m.id))

  const raster = known.has(sel.raster) ? sel.raster
    : known.has(DEFAULT_RASTER) ? DEFAULT_RASTER
    : catalog[0].id

  // Заливка вычитается из набора линий: контур поля, которое ты же и красишь,
  // ничего не добавляет — он ложится ровно по границе своих же тонов.
  const iso = catalog.map(m => m.id).filter(id => sel.iso.includes(id) && id !== raster)

  const same = raster === sel.raster
    && iso.length === sel.iso.length
    && iso.every((id, i) => id === sel.iso[i])
  return same && sel.zero === sel.zero ? { raster, iso, zero: sel.zero } : { raster, iso, zero: sel.zero }
}

/**
 * Глубина — свойство МЕХАНИЗМА, а не имени слоя.
 *
 * Второе слагаемое не косметика: при заливке `upw_topo` и линиях `upw_vertical`
 * линии всё равно лежат НА горизонте, и погашенный глубиномер оставил бы их
 * неатрибутируемыми — человек читал бы поле, не зная, на какой оно глубине.
 */
export function depthApplicable(sel: UpwellingSelection, catalog: Mechanism[]): boolean {
  if (catalog.length === 0) return false
  const byId = new Map(catalog.map(m => [m.id, m]))
  if (byId.get(sel.raster)?.has_depth) return true
  return sel.iso.some(id => byId.get(id)?.has_depth === true)
}

/** Горизонты, на которых лежат поля механизмов. */
export const MECH_DEPTHS = [10, 20, 30, 50] as const

/** Главный горизонт карты: основание слоя Экмана и ходовая глубина сноса. */
export const MECH_DEFAULT_DEPTH = 30

/**
 * Зажать глубину ссылки НА ВХОДЕ в раздел, а не в каждом рендере.
 *
 * `depth=0` в старой ссылке — не выбор человека, а след раздела, где глубиномер
 * был погашен принудительно. Но человек обязан иметь право поставить 0
 * сознательно и увидеть, что вертикального тока на поверхности почти нет
 * (замер: 99,5 % клеток слабее 1 м/сут — это физика, а не пропажа данных).
 * Непрерывный зажим сделал бы 0 м недостижимым и превратил бы честную оговорку
 * легенды в непроверяемое утверждение.
 */
export function snapDepthOnEnter(depth: number): number {
  return depth === 0 ? MECH_DEFAULT_DEPTH : depth
}

/** Ближайший ПОСЧИТАННЫЙ горизонт к выбранному глубиномером.
 *
 *  У глубиномера кабинета семнадцать уровней, у полей — четыре. Пустая карта
 *  при живом поле читалась бы как «здесь ничего нет».
 *
 *  `available` — горизонты, на которых поле механизма за эту дату ДЕЙСТВИТЕЛЬНО
 *  лежит. Не у всех их четыре: дивергенция интегрируется к 30 и 50 м, и
 *  глубиномер на 10 м отдал бы пустую карту при кнопке «Загрузить», которая её
 *  не исправит — поле на этом горизонте не считается вовсе. Немой отказ с
 *  кнопкой, ведущей в никуда, хуже честного показа соседнего горизонта.
 *
 *  Пусто (список ещё не приехал) — прежние четыре: ошибиться горизонтом лучше,
 *  чем не показать ничего.
 */
export function nearestMechDepth(
  depth: number,
  available?: readonly number[] | null,
): number {
  const scale = available && available.length ? available : MECH_DEPTHS
  // Ничья решается в пользу БОЛЕЕ ГЛУБОКОГО горизонта: механизмы про то, что
  // поднимается снизу, а у поверхности вертикальный ток и так стремится к нулю.
  return [...scale].sort((a, b) => a - b)
    .reduce((a, b) => (Math.abs(b - depth) <= Math.abs(a - depth) ? b : a))
}

/** Старая ссылка `?model=upwelling` открывает ЭТАЛОН, а не замороженный индекс.
 *
 *  Она неотличима от свежего входа в раздел, и оставив дефолтом замороженный
 *  индекс, мы оставили бы лицом раздела ровно то, что океанолог назвал неверным.
 *  Подмена при этом называет себя: легенда пишет имя механизма и единицы, тогда
 *  как у старого индекса легенда безразмерная. */
export function defaultSelection(): UpwellingSelection {
  return { raster: DEFAULT_RASTER, iso: [], zero: true }
}
